import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, Card, CardContent, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { fetchRawStockReport } from '../../redux/actions/rawStock/rawStockAction';
import RawMaterialStockReportTable from '../../components/report/RawMaterialStockReportTable';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { fetchStocks } from '../../api/stock/stockApi';
import ReportTitle from '../../components/common/ReportTitle';
import { fetchPurities } from '../../api/purity/purityApi';
import { fetchColors } from '../../api/color/colorApi';
import { fetchStones } from '../../api/stone/stoneApi';
import { fetchRawStockCodes } from '../../api/rawStockCode/rawStockCodeApi';

const initialFilters = {
  date_from: '',
  date_to: '',
  stock_code: '',
  stock_type: '',
  karatage: '',
  color: '',
  stone_type: ''
};

const formatCsvData = (data) => {
  const header = [
    'Date', 'Stock Code', 'Stock Name', 'Stock Type', 'Karatage', 'Color', 'Stone Type', 'Qty', 'Weight', 'Pure Weight', 'Remarks'
  ];
  const rows = data.map(row => [
    row.date ? new Date(row.date).toLocaleDateString() : '',
    row.stock_code,
    row.stock_name,
    row.stock_type,
    row.karatage,
    row.color,
    row.stone_type,
    row.qty,
    row.weight,
    row.pure_weight,
    row.remarks
  ].join(','));
  return [header.join(','), ...rows].join('\n');
};

const RawMaterialStockReportPage = () => {
  const dispatch = useDispatch();
  const { rows, totals, loading, error } = useSelector(state => state.rawStockReport);
  const [filters, setFilters] = useState(initialFilters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(filters);
  const [stockTypes, setStockTypes] = useState([]);
  const [purities, setPurities] = useState([]);
  const [colors, setColors] = useState([]);
  const [stoneTypes, setStoneTypes] = useState([]);
  const [stockCodes, setStockCodes] = useState([]);

  useEffect(() => {
    dispatch(fetchRawStockReport(filters));
    // Fetch master data for dropdowns
    fetchStocks().then(res => setStockTypes(res.data)).catch(() => setStockTypes([]));
    fetchPurities().then(res => setPurities(res.data)).catch(() => setPurities([]));
    fetchColors().then(res => setColors(res.data)).catch(() => setColors([]));
    fetchStones().then(res => setStoneTypes(res.data)).catch(() => setStoneTypes([]));
    fetchRawStockCodes().then(res => setStockCodes(res.data)).catch(() => setStockCodes([]));
  }, [dispatch, filters]);

  const handleOpenFilterDialog = () => {
    setPendingFilters(filters);
    setFilterDialogOpen(true);
  };
  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };
  const handlePendingInputChange = (e) => {
    setPendingFilters({ ...pendingFilters, [e.target.name]: e.target.value });
  };
  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    setFilterDialogOpen(false);
  };
  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPendingFilters(initialFilters);
    setFilterDialogOpen(false);
  };

  const handleExport = () => {
    const csvData = formatCsvData(rows);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'raw_material_stock_report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <ReportTitle>Current Raw Material Stock Detail Report</ReportTitle>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FilterListIcon />}
          onClick={handleOpenFilterDialog}
          sx={{ mr: 2 }}
        >
          Filters
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
      </Box>
      <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Filter Raw Material Stock Report
          <IconButton
            aria-label="close"
            onClick={handleCloseFilterDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="From Date"
                type="date"
                name="date_from"
                value={pendingFilters.date_from}
                onChange={handlePendingInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="To Date"
                type="date"
                name="date_to"
                value={pendingFilters.date_to}
                onChange={handlePendingInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Stock Code"
                name="stock_code"
                value={pendingFilters.stock_code}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {stockCodes.map((sc) => (
                  <MenuItem key={sc.id} value={sc.stock_code}>{sc.stock_code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Stock Type"
                name="stock_type"
                value={pendingFilters.stock_type}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {stockTypes.map((s) => (
                  <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Karatage"
                name="karatage"
                value={pendingFilters.karatage}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {purities.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Color"
                name="color"
                value={pendingFilters.color}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {colors.map((c) => (
                  <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Stone Type"
                name="stone_type"
                value={pendingFilters.stone_type || ''}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {stoneTypes.map((s) => (
                  <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters} color="secondary">Clear</Button>
          <Button onClick={handleApplyFilters} color="primary" variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Qty</Typography>
              <Typography variant="h6" color="inherit">{totals.total_qty}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Weight</Typography>
              <Typography variant="h6" color="inherit">{Number(totals.total_weight).toFixed(3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Pure Weight</Typography>
              <Typography variant="h6" color="inherit">{Number(totals.total_pure_weight).toFixed(3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <RawMaterialStockReportTable reportData={rows} loading={loading} error={error} />
    </Box>
  );
};

export default RawMaterialStockReportPage; 